/**
 * Life in the air: petals and leaves drifting down, fireflies after dark,
 * gulls wheeling over the shore, paper lanterns floating on the canals,
 * chimney smoke and steam from food carts.
 *
 * Every particle is a pure function of time and its index, so there is no
 * state to keep and nothing to sync; only what is on screen is drawn.
 */
import { footprint, hash01, type Ambience, type Prop } from '../../../shared/world.ts'
import { chimneyTop } from './buildings.ts'
import { alpha } from './color.ts'
import { glowAt, iso, type Pt } from './iso.ts'

type Ctx = CanvasRenderingContext2D
interface Box { x: number; y: number; w: number; h: number }

const inView = (p: Pt, v: Box, pad = 40) => p.x > v.x - pad && p.x < v.x + v.w + pad && p.y > v.y - pad && p.y < v.y + v.h + pad

export interface Emitter { x: number; y: number; z: number; kind: 'smoke' | 'steam'; seed: number }

/** Smoke and steam sources in a room: cottage chimneys and food carts. */
export function emitters(props: Prop[]): Emitter[] {
  const out: Emitter[] = []
  for (const p of props) {
    if (p.kind === 'building') {
      const c = chimneyTop(p)
      if (c) out.push({ ...c, kind: 'smoke', seed: Math.floor(p.x * 7 + p.y * 3) })
    } else if (p.kind === 'cart') {
      const f = footprint(p)
      out.push({ x: p.x + f.w / 2, y: p.y + f.d / 2, z: 1.9, kind: 'steam', seed: Math.floor(p.x * 5 + p.y) })
    }
  }
  return out
}

/** Smoke, steam, falling petals and leaves, and gulls: drawn over the scene, before the night wash. */
export function drawAir(ctx: Ctx, amb: Ambience[], em: Emitter[], view: Box, t: number): void {
  for (const e of em) {
    const base = iso(e.x, e.y, e.z)
    if (!inView(base, view, 80)) continue
    const n = e.kind === 'smoke' ? 6 : 4
    for (let i = 0; i < n; i++) {
      const k = (t * (e.kind === 'smoke' ? 0.22 : 0.5) + i / n + e.seed * 0.13) % 1
      const drift = Math.sin(t * 0.8 + i + e.seed) * 4 + k * 10
      const r = (e.kind === 'smoke' ? 4 : 3) + k * (e.kind === 'smoke' ? 9 : 6)
      ctx.fillStyle = e.kind === 'smoke' ? `rgba(235,232,240,${0.42 * (1 - k)})` : `rgba(255,255,255,${0.55 * (1 - k)})`
      ctx.beginPath(); ctx.arc(base.x + drift, base.y - k * (e.kind === 'smoke' ? 46 : 24), r, 0, Math.PI * 2); ctx.fill()
    }
  }
  for (const a of amb) {
    if (a.kind === 'petals' || a.kind === 'leaves') {
      const n = Math.round((a.w * a.d) / 14)
      const H = 4
      for (let i = 0; i < n; i++) {
        const speed = 0.12 + hash01(i, 7, a.x) * 0.1
        const k = (t * speed + hash01(i, 3, a.y)) % 1
        const px = a.x + hash01(i, 1, a.x) * a.w + Math.sin(t * 0.9 + i) * 0.4 + k * 1.2
        const py = a.y + hash01(i, 2, a.y) * a.d + k * 0.6
        const p = iso(px, py, H * (1 - k))
        if (!inView(p, view)) continue
        const spin = Math.sin(t * 3 + i)
        ctx.fillStyle = a.kind === 'petals' ? (i % 3 ? '#ffc6d9' : '#ffe6ef') : ['#d98c3c', '#c9a23a', '#b0503c'][i % 3]!
        ctx.beginPath(); ctx.ellipse(p.x, p.y, a.kind === 'petals' ? 2.6 : 3.4, 1.4 + Math.abs(spin) * 0.8, spin, 0, Math.PI * 2); ctx.fill()
      }
    } else if (a.kind === 'gulls') {
      for (let i = 0; i < 5; i++) {
        const r = 4 + hash01(i, 9, a.x) * 6
        const ang = t * (0.12 + hash01(i, 4, a.y) * 0.1) * (i % 2 ? 1 : -1) + i * 1.7
        const p = iso(a.x + a.w / 2 + Math.cos(ang) * r, a.y + a.d * 0.65 + Math.sin(ang) * r * 0.6, 5 + hash01(i, 5) * 2)
        if (!inView(p, view)) continue
        const flap = Math.sin(t * 7 + i) * 4
        ctx.strokeStyle = 'rgba(70,70,90,0.85)'; ctx.lineWidth = 1.8; ctx.lineCap = 'round'
        ctx.beginPath(); ctx.moveTo(p.x - 8, p.y - flap); ctx.quadraticCurveTo(p.x - 4, p.y - 4 - flap * 0.3, p.x, p.y); ctx.quadraticCurveTo(p.x + 4, p.y - 4 - flap * 0.3, p.x + 8, p.y - flap); ctx.stroke()
      }
    }
  }
}

/** Paper lanterns afloat on the canals: drawn with the water, so bridges and houses hide them. */
export function drawFloatingLanterns(ctx: Ctx, amb: Ambience[], water: Array<[number, number, boolean]>, view: Box, t: number, day: number): void {
  for (const a of amb) {
    if (a.kind === 'sparkles') {
      let i = 0
      for (const [x, y] of water) {
        if (x < a.x || y < a.y || x >= a.x + a.w || y >= a.y + a.d) continue
        if (hash01(x, y, 31) > 0.12) continue
        i++
        const p = iso(x + 0.5 + Math.sin(t * 0.3 + i) * 0.3, y + 0.5 + Math.cos(t * 0.25 + i) * 0.3, -0.24)
        if (!inView(p, view)) continue
        const bob = Math.sin(t * 2 + i) * 1
        ctx.fillStyle = 'rgba(20,40,70,0.25)'; ctx.beginPath(); ctx.ellipse(p.x, p.y + 2, 6, 2.5, 0, 0, Math.PI * 2); ctx.fill()
        ctx.fillStyle = i % 3 ? '#e0443a' : '#ffb347'
        ctx.beginPath(); ctx.moveTo(p.x - 5, p.y + bob); ctx.lineTo(p.x + 5, p.y + bob); ctx.lineTo(p.x + 3, p.y - 7 + bob); ctx.lineTo(p.x - 3, p.y - 7 + bob); ctx.closePath(); ctx.fill()
        if (day < 0.8) glowAt(ctx, { x: p.x, y: p.y - 4 + bob }, 14, 10, '#ffb347', 0.5 * (1 - day))
      }
    }
  }
}

/** Fireflies: only after dusk, drawn additively in the night pass. */
export function drawFireflies(ctx: Ctx, amb: Ambience[], view: Box, t: number, night: number): void {
  if (night < 0.3) return
  for (const a of amb) {
    if (a.kind !== 'fireflies') continue
    const n = Math.round((a.w * a.d) / 30)
    for (let i = 0; i < n; i++) {
      const bx = a.x + hash01(i, 11, a.x) * a.w, by = a.y + hash01(i, 12, a.y) * a.d
      const p = iso(bx + Math.sin(t * 0.4 + i) * 1.2, by + Math.cos(t * 0.33 + i * 1.3) * 1.2, 0.6 + Math.sin(t * 0.9 + i) * 0.35)
      if (!inView(p, view)) continue
      const blink = Math.max(0, Math.sin(t * 2.2 + i * 2.7))
      if (blink < 0.05) continue
      glowAt(ctx, p, 9, 9, '#d9ff7a', 0.9 * blink * night)
      ctx.fillStyle = alpha('#f4ffb0', blink * night)
      ctx.fillRect(p.x - 1, p.y - 1, 2, 2)
    }
  }
}
